import {
  COMPONENT_VALUE_TYPES as types,
  DIRECTIONS,
} from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  editor?: object;
  layout?: 'Horizontal' | 'Two Cards per Row';
  background?: {
    useBackgroundColor?: boolean;
    backgroundColor?: string;
    desktopImage?: string;
    tabletImage?: string;
    mobileImage?: string;
  };
  sectionTitle?: {
    text?: string;
    color?: string;
  };
  cards?: {
    title?: string;
    description?: string;
    link?: string;
    linkText?: string;
    image?: string;
  }[];
  titleColor?: string;
  descriptionColor?: string;
  linkColor?: string;
  borderStyle?: 'Border' | 'Divider';
  borderColor?: string;
  cardBackgroundColor?: string;
  animation?: {
    enabled?: boolean;
    type?: 'FADE' | 'SLIDE' | 'SCALE';
    direction?: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
    duration?: number | string;
    initialDelay?: number | string;
    stagger?: number | string;
    animateHeader?: boolean;
    animateBlocks?: boolean;
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'layout',
    label: 'Layout',
    type: types.ENUM,
    options: ['Horizontal', 'Two Cards per Row'],
  },
  {
    key: 'background',
    label: 'Background',
    type: types.OBJECT,
    children: [
      {
        key: 'useBackgroundColor',
        label: 'Use Background Color',
        type: types.BOOLEAN,
      },
      {
        key: 'backgroundColor',
        label: 'Background Color',
        type: types.COLOR_PICKER,
      },
      {
        key: 'desktopImage',
        label: 'Desktop Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'tabletImage',
        label: 'Tablet Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'mobileImage',
        label: 'Mobile Background Image',
        type: types.S3_ASSET_URL,
      },
    ],
  },
  {
    key: 'sectionTitle',
    label: 'Section Title',
    type: types.OBJECT,
    children: [
      {
        key: 'text',
        label: 'Title Text',
        type: types.TEXT,
      },
      {
        key: 'color',
        label: 'Title Color',
        type: types.COLOR_PICKER,
      },
    ],
  },
  {
    key: 'cards',
    label: 'Cards',
    type: types.ARRAY,
    children: [
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'description',
        label: 'Description',
        type: types.TEXTAREA,
      },
      {
        key: 'link',
        label: 'Link URL',
        type: types.TEXT,
      },
      {
        key: 'linkText',
        label: 'Link Text',
        type: types.TEXT,
      },
      {
        key: 'image',
        label: 'Image',
        type: types.S3_ASSET_URL,
      },
    ],
  },
  {
    key: 'titleColor',
    label: 'Card Title Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'descriptionColor',
    label: 'Card Description Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'linkColor',
    label: 'Card Link Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'borderStyle',
    label: 'Border Style',
    type: types.ENUM,
    options: ['Border', 'Divider'],
  },
  {
    key: 'borderColor',
    label: 'Border/Divider Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'cardBackgroundColor',
    label: 'Card Background Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'animation',
    label: 'Animation',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enable Animation',
        type: types.BOOLEAN,
      },
      {
        key: 'type',
        label: 'Animation Type',
        type: types.ENUM,
        options: ['FADE', 'SLIDE', 'SCALE'],
      },
      {
        key: 'direction',
        label: 'Direction (for Slide)',
        type: types.ENUM,
        options: DIRECTIONS,
      },
      {
        key: 'duration',
        label: 'Duration (s)',
        type: types.TEXT,
      },
      {
        key: 'initialDelay',
        label: 'Initial Delay (s)',
        type: types.TEXT,
      },
      {
        key: 'stagger',
        label: 'Stagger Delay (s)',
        type: types.TEXT,
        tooltip: 'Delay between each item\'s animation start.',
      },
      {
        key: 'animateHeader',
        label: 'Animate Header',
        type: types.BOOLEAN,
      },
      {
        key: 'animateBlocks',
        label: 'Animate Cards',
        type: types.BOOLEAN,
      },
    ],
  },
];

const defaultProps: Props = {
  layout: 'Horizontal',
  background: {
    useBackgroundColor: false,
    backgroundColor: '#000000',
    desktopImage: 'https://genesis-cdn.ff.com/cxc10/card-section-default-bg-default.jpg',
    tabletImage: '',
    mobileImage: '',
  },
  sectionTitle: {
    text: 'Advantages of C10 Treasury Management',
    color: '#C6AA84',
  },
  cards: [
    {
      title: 'Treasury & C10',
      description: 'Value Bedrock - Long-term Value Anchor and Growth Engine of the Entire Ecosystem',
      link: 'https://www.c10index.com/en/',
      linkText: 'Learn More >',
      image: 'https://genesis-cdn.ff.com/cxc10/original/chart-up.png',
    },
    {
      title: 'BesTrade DeAI Agent',
      description: 'Users, Information, and Value Hub. The entry point to Web3 trading and value-linked services; the mother exchange AI Agent helping users achieve optimal trades.',
      link: 'https://www.c10index.com/en/',
      linkText: 'Learn More >',
      image: 'https://genesis-cdn.ff.com/cxc10/original/compass.png',
    },
    {
      title: 'AIxC Ecosystem Build-Up',
      description: 'Value Maximization Carrier',
      link: 'https://www.c10index.com/en/',
      linkText: 'Learn More >',
      image: 'https://genesis-cdn.ff.com/cxc10/original/speed-meter.png',
    },
  ],
  titleColor: 'rgba(198, 170, 132, 1)',
  descriptionColor: 'rgba(181, 181, 181, 1)',
  linkColor: 'rgba(181, 181, 181, 1)',
  borderStyle: 'Border',
  borderColor: '#C6AA84',
  cardBackgroundColor: 'transparent',
  animation: {
    enabled: false,
    type: 'SLIDE',
    direction: 'UP',
    duration: 0.8,
    initialDelay: 0,
    stagger: 0.2,
    animateHeader: true,
    animateBlocks: true,
  },
};

export default {
  title: 'C10 Card Section',
  id: 'C10CardSection',
  propsSchema,
  defaultProps,
};
